import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Mail, Lock, ArrowLeft } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email.trim().toLowerCase(), password);
      navigate(localStorage.getItem('pending_gift_token') ? `/cadeau/${localStorage.getItem('pending_gift_token')}` : '/menu');
    } catch (err) {
      setError(err.message || 'Email ou mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lato-screen min-h-screen bg-[#FAF9F6] flex flex-col px-6 py-8">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate('/')}
        className="p-2 -ml-2 text-gray-400 hover:text-[#F79AAE] transition-colors w-fit"
        aria-label="Retour"
      >
        <ArrowLeft size={20} />
      </button>

      <div className="flex-1 flex flex-col justify-center max-w-sm mx-auto w-full">
        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-light text-gray-800">Connexion</h1>
          <div className="mt-3 w-8 h-[1px] bg-[#F79AAE] mx-auto"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email */}
          <div>
            <label htmlFor="login-email" className="block text-sm text-gray-500 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#F79AAE] transition-colors text-gray-700"
                placeholder="votre@email.com"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                inputMode="email"
                required
                data-testid="login-email-input"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label htmlFor="login-password" className="block text-sm text-gray-500 mb-2">Mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
              <input
                id="login-password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-12 pr-12 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#F79AAE] transition-colors text-gray-700"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                data-testid="login-toggle-password"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="text-right text-sm">
            <Link to="/mot-de-passe-oublie" className="text-[#F79AAE]" data-testid="forgot-password-link">
              Mot de passe oublié ?
            </Link>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-400 text-center">{error}</p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-[#F79AAE] text-white rounded-xl text-sm hover:bg-[#e8868d] transition-colors disabled:opacity-50"
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>

        {/* Register link */}
        <p className="mt-8 text-center text-sm text-gray-500">
          Pas de compte ?{' '}
          <Link to="/register" className="text-[#F79AAE]">
            Créer un compte
          </Link>
        </p>

        {/* Mode visiteur */}
        <button
          type="button"
          onClick={() => navigate('/')}
          data-testid="visitor-mode-button"
          className="mt-4 w-full text-center text-sm text-gray-400 underline underline-offset-4 decoration-gray-300 hover:text-gray-600 transition-colors"
        >
          Continuer sans compte
        </button>
      </div>
    </div>
  );
};

export default Login;
